import React, { useEffect, useState, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  fetchLevels,
  addLevel,
  updateLevel,
  deleteLevel,
  reorderLevels,
  saveLevelPercentages,
  clearError,
} from "../../redux/slices/mlmTreeLevelSlice";
import api from "../../api/axios";
import { Modal, Button, Badge, ProgressBar } from "react-bootstrap";
import { toast } from "react-toastify";
import DeleteConfirmationModal from "../../components/DeleteConfirmationModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import "./MlmLevelsConfigPage.css";

// Default template hierarchy
const DEFAULT_TEMPLATE = [
  { level_name: "IUG Head Office", level_order: 1, commission_percentage: 5 },
  { level_name: "IUG Regional Office", level_order: 2, commission_percentage: 5 },
  { level_name: "IUG Branch Office", level_order: 3, commission_percentage: 5 },
  { level_name: "Executive Director (ED)", level_order: 4, commission_percentage: 15 },
  { level_name: "Deputy Director (DD)", level_order: 5, commission_percentage: 20 },
  { level_name: "Assistant Director (AD)", level_order: 6, commission_percentage: 20 },
  { level_name: "Consultant", level_order: 7, commission_percentage: 30 },
];

const LEVEL_COLORS = [
  "#6366f1", // indigo
  "#8b5cf6", // violet
  "#a855f7", // purple
  "#d946ef", // fuchsia
  "#ec4899", // pink
  "#f43f5e", // rose
  "#f97316", // orange
  "#eab308", // yellow
  "#22c55e", // green
  "#06b6d4", // cyan
];

const MlmLevelsConfigPage = () => {
  const dispatch = useDispatch();
  const { levels, totalPercentage, isValid, status, error } = useSelector(
    (state) => state.mlmTreeLevels
  );

  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState("");
  const [selectedServiceId, setSelectedServiceId] = useState("");
  const [selectedProductId, setSelectedProductId] = useState("");
  const [catalogServices, setCatalogServices] = useState([]);

  const catalogProducts = useMemo(() => {
    const service = catalogServices.find((s) => s.id === selectedServiceId);
    return service?.products ?? [];
  }, [catalogServices, selectedServiceId]);

  useEffect(() => {
    if (!selectedCompanyId) {
      setCatalogServices([]);
      return;
    }

    api
      .get(`/api/admin/mlm-catalog/${selectedCompanyId}`)
      .then((res) => setCatalogServices(res.data.services ?? []))
      .catch(() => {
        setCatalogServices([]);
        toast.error("Failed to load product catalog");
      });
  }, [selectedCompanyId]);

  useEffect(() => {
    if (catalogServices.length > 0 && !catalogServices.find((s) => s.id === selectedServiceId)) {
      setSelectedServiceId(catalogServices[0].id);
    }
    const products = catalogServices.find((s) => s.id === selectedServiceId)?.products ?? [];
    if (products.length > 0 && !products.find((p) => p.id === selectedProductId)) {
      setSelectedProductId(products[0].id);
    }
  }, [catalogServices, selectedServiceId, selectedProductId]);

  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingLevel, setEditingLevel] = useState(null);
  const [deletingLevel, setDeletingLevel] = useState(null);
  const [formData, setFormData] = useState({
    level_name: "",
    level_order: "",
    commission_percentage: "",
  });
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const [showTemplateConfirm, setShowTemplateConfirm] = useState(false);

  // "Edit percentages" mode: change all levels together, saved in one request.
  const [editingPercentages, setEditingPercentages] = useState(false);
  const [draftPercentages, setDraftPercentages] = useState({});
  const [savingPercentages, setSavingPercentages] = useState(false);

  // Load companies on mount
  useEffect(() => {
    const loadCompanies = async () => {
      try {
        const res = await api.get("/api/companies");
        setCompanies(res.data);
        if (res.data.length > 0) {
          setSelectedCompanyId(res.data[0].id);
        }
      } catch (err) {
        toast.error("Failed to load companies");
      }
    };
    loadCompanies();
  }, []);

  // Load levels when company, service, or product changes
  useEffect(() => {
    setEditingPercentages(false);
    if (selectedCompanyId) {
      dispatch(fetchLevels({ 
        companyId: selectedCompanyId, 
        serviceId: selectedServiceId, 
        productId: selectedProductId 
      }));
    }
  }, [selectedCompanyId, selectedServiceId, selectedProductId, dispatch]);

  // Handle errors
  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  const handleCompanyChange = (e) => {
    setSelectedCompanyId(e.target.value);
  };

  const handleServiceChange = (e) => {
    setSelectedServiceId(e.target.value);
  };

  const handleProductChange = (e) => {
    setSelectedProductId(e.target.value);
  };

  // ─── Add Level ───

  const openAddModal = () => {
    const nextOrder =
      levels.length > 0
        ? Math.max(...levels.map((l) => l.level_order)) + 1
        : 1;
    setFormData({
      level_name: "",
      level_order: nextOrder,
      commission_percentage: "",
    });
    setShowAddModal(true);
  };

  const handleAddSubmit = async (e) => {
    e.preventDefault();
    if (!formData.level_name || !formData.commission_percentage) {
      toast.error("Please fill in all required fields!");
      return;
    }
    try {
      await dispatch(
        addLevel({
          company_id: selectedCompanyId,
          service_id: selectedServiceId,
          product_id: selectedProductId,
          ...formData,
          level_order: parseInt(formData.level_order),
          commission_percentage: parseFloat(formData.commission_percentage),
        })
      ).unwrap();
      toast.success("Level added successfully!");
      setShowAddModal(false);
    } catch {
      // Error toast comes from the slice (state.error); keep the form open.
    }
  };

  // ─── Edit Level ───

  const openEditModal = (level) => {
    setEditingLevel(level);
    setFormData({
      level_name: level.level_name,
      level_order: level.level_order,
      commission_percentage: level.commission_percentage,
    });
    setShowEditModal(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      await dispatch(
        updateLevel({
          id: editingLevel.id,
          data: {
            level_name: formData.level_name,
            level_order: parseInt(formData.level_order),
            commission_percentage: parseFloat(formData.commission_percentage),
          },
        })
      ).unwrap();
      toast.success("Level updated successfully!");
      setShowEditModal(false);
      setEditingLevel(null);
    } catch {
      // Error toast comes from the slice (state.error); keep the form open.
    }
  };

  // ─── Delete Level ───

  const handleDeleteClick = (level) => {
    setDeletingLevel(level);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deletingLevel) return;
    try {
      await dispatch(deleteLevel(deletingLevel.id)).unwrap();
      toast.success("Level deleted successfully!");
      setShowDeleteModal(false);
      setDeletingLevel(null);
    } catch {
      // Error toast comes from the slice (state.error); keep the form open.
    }
  };

  // ─── Reorder (move up/down) ───

  const handleMoveUp = (index) => {
    if (index === 0) return;
    const newLevels = [...levels];
    const currentOrder = newLevels[index].level_order;
    const prevOrder = newLevels[index - 1].level_order;
    const reorderedLevels = newLevels.map((l, i) => {
      if (i === index) return { id: l.id, level_order: prevOrder };
      if (i === index - 1) return { id: l.id, level_order: currentOrder };
      return { id: l.id, level_order: l.level_order };
    });
    dispatch(
      reorderLevels({
        companyId: selectedCompanyId,
        serviceId: selectedServiceId,
        productId: selectedProductId,
        levels: reorderedLevels,
      })
    );
  };

  const handleMoveDown = (index) => {
    if (index === levels.length - 1) return;
    const newLevels = [...levels];
    const currentOrder = newLevels[index].level_order;
    const nextOrder = newLevels[index + 1].level_order;
    const reorderedLevels = newLevels.map((l, i) => {
      if (i === index) return { id: l.id, level_order: nextOrder };
      if (i === index + 1) return { id: l.id, level_order: currentOrder };
      return { id: l.id, level_order: l.level_order };
    });
    dispatch(
      reorderLevels({
        companyId: selectedCompanyId,
        serviceId: selectedServiceId,
        productId: selectedProductId,
        levels: reorderedLevels,
      })
    );
  };

  // ─── Edit all percentages together ───

  // Whole basis points avoid float drift (33.33 + 33.33 + 33.34 must be exactly 100).
  const toBasisPoints = (value) => Math.round((parseFloat(value) || 0) * 100);

  const draftTotalBp = useMemo(
    () =>
      levels
        .filter((l) => l.is_active)
        .reduce((sum, l) => sum + toBasisPoints(draftPercentages[l.id] ?? l.commission_percentage), 0),
    [levels, draftPercentages]
  );
  const draftTotal = draftTotalBp / 100;
  // A live product (already 100%) must stay at exactly 100%.
  const draftError =
    draftTotalBp > 10000
      ? "Total is over 100%."
      : isValid && draftTotalBp !== 10000
        ? "These levels are in use for sales, so the total must stay exactly 100%."
        : null;

  const startEditingPercentages = () => {
    setDraftPercentages(
      Object.fromEntries(levels.map((l) => [l.id, String(l.commission_percentage)]))
    );
    setEditingPercentages(true);
  };

  const cancelEditingPercentages = () => {
    setEditingPercentages(false);
    setDraftPercentages({});
  };

  const saveAllPercentages = async () => {
    if (draftError) {
      toast.error(draftError);
      return;
    }
    setSavingPercentages(true);
    try {
      await dispatch(
        saveLevelPercentages({
          companyId: selectedCompanyId,
          serviceId: selectedServiceId,
          productId: selectedProductId,
          levels: levels.map((l) => ({
            id: l.id,
            commission_percentage: parseFloat(draftPercentages[l.id] ?? l.commission_percentage) || 0,
          })),
        })
      ).unwrap();
      toast.success("Percentages saved!");
      setEditingPercentages(false);
      setDraftPercentages({});
    } catch (err) {
      toast.error(err?.message || "Failed to save percentages");
    }
    setSavingPercentages(false);
  };

  // ─── Load Default Template ───
  const confirmLoadDefaultTemplate = async () => {
    setShowTemplateConfirm(false);
    setLoadingTemplate(true);
    if (levels.length > 0) {
      // Delete existing levels first
      try {
        for (const level of levels) {
          await dispatch(deleteLevel(level.id)).unwrap();
        }
      } catch {
        toast.error("Failed to clear existing levels");
        setLoadingTemplate(false);
        return;
      }
    }

    try {
      for (const tpl of DEFAULT_TEMPLATE) {
        await dispatch(
          addLevel({ 
            company_id: selectedCompanyId, 
            service_id: selectedServiceId,
            product_id: selectedProductId,
            ...tpl 
          })
        ).unwrap();
      }
      toast.success("Default template loaded successfully!");
    } catch {
      // Error toast comes from the slice (state.error); keep the form open.
    }
    setLoadingTemplate(false);
  };

  const loadDefaultTemplate = async () => {
    if (levels.length > 0) {
      setShowTemplateConfirm(true);
    } else {
      confirmLoadDefaultTemplate();
    }
  };

  const getPercentageBarVariant = () => {
    if (Math.abs(totalPercentage - 100) < 0.01) return "success";
    if (totalPercentage > 100) return "danger";
    return "warning";
  };

  const getPercentageColor = () => {
    if (Math.abs(totalPercentage - 100) < 0.01) return "#22c55e";
    if (totalPercentage > 100) return "#ef4444";
    return "#f59e0b";
  };

  const getLevelColor = (index) => LEVEL_COLORS[index % LEVEL_COLORS.length];

  return (
    <div className="mlm-levels-page">
      <div className="container mt-4">
        {/* ─── Header ─── */}
        <div className="mlm-levels-header mb-4">
          <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
            <div>
              <h3 className="fw-bold text-gradient mb-1">
                <span className="me-2">🏗️</span>MLM Tree Levels
              </h3>
              <p className="text-muted mb-0">
                Configure the commission tree hierarchy for each company
              </p>
            </div>
            <div className="d-flex gap-2 flex-wrap">
              <select
                className="form-select company-select"
                value={selectedCompanyId}
                onChange={handleCompanyChange}
              >
                {companies.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
              <select
                className="form-select company-select"
                value={selectedServiceId}
                onChange={handleServiceChange}
              >
                {catalogServices.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
              <select
                className="form-select company-select"
                value={selectedProductId}
                onChange={handleProductChange}
              >
                {catalogProducts.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* ─── Action Bar ─── */}
        <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
          <div className="d-flex gap-2">
            <button
              className="btn btn-primary btn-sm"
              onClick={openAddModal}
              disabled={!selectedCompanyId || !selectedServiceId || !selectedProductId}
            >
              <i className="uil-plus me-1"></i>Add Level
            </button>
            <button
              className="btn btn-outline-secondary btn-sm"
              onClick={loadDefaultTemplate}
              disabled={!selectedCompanyId || !selectedServiceId || !selectedProductId || loadingTemplate}
            >
              {loadingTemplate ? (
                <>
                  <span className="spinner-border spinner-border-sm me-1"></span>
                  Loading...
                </>
              ) : (
                <>📋 Load Default Template</>
              )}
            </button>
            {editingPercentages ? (
              <>
                <button
                  className="btn btn-success btn-sm"
                  onClick={saveAllPercentages}
                  disabled={savingPercentages || Boolean(draftError)}
                  title={draftError || "Save all percentages"}
                >
                  {savingPercentages ? "Saving..." : `Save all (${draftTotal.toFixed(2)}%)`}
                </button>
                <button
                  className="btn btn-outline-secondary btn-sm"
                  onClick={cancelEditingPercentages}
                  disabled={savingPercentages}
                >
                  Cancel
                </button>
              </>
            ) : (
              <button
                className="btn btn-outline-primary btn-sm"
                onClick={startEditingPercentages}
                disabled={levels.length === 0}
              >
                <i className="uil-percentage me-1"></i>Edit percentages
              </button>
            )}
          </div>
          <Badge
            bg="none"
            className="total-badge"
            style={{
              backgroundColor: getPercentageColor() + "18",
              color: getPercentageColor(),
              border: `1px solid ${getPercentageColor()}40`,
            }}
          >
            Total: {parseFloat(totalPercentage).toFixed(1)}%{" "}
            {isValid ? "✓" : totalPercentage > 100 ? "⚠️ Over" : "⚠️ Under"}
          </Badge>
        </div>

        {editingPercentages && draftError && (
          <div className="alert alert-warning py-2 small mb-3">{draftError}</div>
        )}

        {/* ─── Percentage Progress Bar ─── */}
        {levels.length > 0 && (
          <div className="percentage-bar-container mb-4">
            <ProgressBar
              now={Math.min(totalPercentage, 100)}
              variant={getPercentageBarVariant()}
              animated={!isValid}
              className="percentage-bar"
            />
            <div className="percentage-segments">
              {levels.map((level, i) => (
                <div
                  key={level.id}
                  className="segment-indicator"
                  style={{
                    width: `${Math.min(level.commission_percentage, 100)}%`,
                    backgroundColor: getLevelColor(i) + "30",
                    borderRight: `2px solid ${getLevelColor(i)}`,
                  }}
                  title={`${level.level_name}: ${level.commission_percentage}%`}
                />
              ))}
            </div>
          </div>
        )}

        {/* ─── Visual Hierarchy Flow ─── */}
        {levels.length > 0 && (
          <div className="hierarchy-flow mb-4">
            {levels.map((level, i) => (
              <React.Fragment key={level.id}>
                <div className="hierarchy-node">
                  <div
                    className="hierarchy-chip"
                    style={{
                      borderColor: getLevelColor(i),
                      backgroundColor: getLevelColor(i) + "12",
                    }}
                  >
                    <span
                      className="chip-dot"
                      style={{ backgroundColor: getLevelColor(i) }}
                    />
                    <span className="chip-label">{level.level_name}</span>
                    <Badge bg="none" className="chip-pct" style={{ color: getLevelColor(i) }}>
                      {parseFloat(level.commission_percentage).toFixed(1)}%
                    </Badge>
                  </div>
                </div>
                {i < levels.length - 1 && (
                  <div className="hierarchy-arrow">
                    <svg width="24" height="20" viewBox="0 0 24 20">
                      <path
                        d="M12 0 L12 14 M6 10 L12 16 L18 10"
                        stroke={getLevelColor(i)}
                        strokeWidth="2"
                        fill="none"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        opacity="0.5"
                      />
                    </svg>
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        )}

        {/* ─── Levels Table ─── */}
        <div className="levels-card">
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead>
                <tr>
                  <th style={{ width: "60px" }}>Order</th>
                  <th>Level Name</th>
                  <th style={{ width: "150px" }}>Commission %</th>
                  <th style={{ width: "100px" }}>Assigned</th>
                  <th style={{ width: "100px" }}>Status</th>
                  <th style={{ width: "180px" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {status === "loading" && levels.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="text-center py-5">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </td>
                  </tr>
                ) : levels.length > 0 ? (
                  levels.map((level, index) => (
                    <tr key={level.id} className="level-row">
                      <td>
                        <div className="d-flex align-items-center gap-1">
                          <div className="order-controls">
                            <button
                              className="btn btn-link btn-sm p-0 order-btn"
                              onClick={() => handleMoveUp(index)}
                              disabled={index === 0}
                              title="Move up"
                            >
                              ▲
                            </button>
                            <button
                              className="btn btn-link btn-sm p-0 order-btn"
                              onClick={() => handleMoveDown(index)}
                              disabled={index === levels.length - 1}
                              title="Move down"
                            >
                              ▼
                            </button>
                          </div>
                          <span className="order-num">{level.level_order}</span>
                        </div>
                      </td>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <span
                            className="level-dot"
                            style={{ backgroundColor: getLevelColor(index) }}
                          />
                          <span className="fw-semibold">{level.level_name}</span>
                        </div>
                      </td>
                      <td>
                        {editingPercentages ? (
                          <div className="input-group input-group-sm">
                            <input
                              type="number"
                              className="form-control"
                              aria-label={`${level.level_name} commission percentage`}
                              value={draftPercentages[level.id] ?? ""}
                              onChange={(e) =>
                                setDraftPercentages({
                                  ...draftPercentages,
                                  [level.id]: e.target.value,
                                })
                              }
                              min="0"
                              max="100"
                              step="0.01"
                            />
                            <span className="input-group-text">%</span>
                          </div>
                        ) : (
                        <div className="pct-cell">
                          <div
                            className="pct-bar"
                            style={{
                              width: `${Math.min(level.commission_percentage, 100)}%`,
                              backgroundColor: getLevelColor(index),
                            }}
                          />
                          <span className="pct-text">
                            {parseFloat(level.commission_percentage).toFixed(1)}%
                          </span>
                        </div>
                        )}
                      </td>
                      <td>
                        <Badge
                          bg={level.nodes_count > 0 ? "primary" : "secondary"}
                          className="assigned-badge"
                        >
                          {level.nodes_count || 0} user{level.nodes_count !== 1 ? "s" : ""}
                        </Badge>
                      </td>
                      <td>
                        <Badge bg={level.is_active ? "success" : "secondary"}>
                          {level.is_active ? "Active" : "Inactive"}
                        </Badge>
                      </td>
                      <td>
                        <div className="d-flex gap-1">
                          <button
                            className="btn btn-sm btn-outline-warning"
                            onClick={() => openEditModal(level)}
                            title="Edit"
                          >
                            <i className="uil-edit"></i>
                          </button>
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => handleDeleteClick(level)}
                            title="Delete"
                            disabled={level.nodes_count > 0}
                          >
                            <i className="uil-trash-alt"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="text-center py-5">
                      <div className="empty-state">
                        <div className="empty-icon">🌳</div>
                        <h5 className="text-muted">No levels configured</h5>
                        <p className="text-muted small mb-3">
                          Start by adding levels or loading the default template
                        </p>
                        <button
                          className="btn btn-outline-primary btn-sm"
                          onClick={loadDefaultTemplate}
                          disabled={!selectedCompanyId || !selectedServiceId || !selectedProductId}
                        >
                          📋 Load Default Template
                        </button>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* ─── Add Modal ─── */}
        <Modal show={showAddModal} onHide={() => setShowAddModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Add New Level</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <form onSubmit={handleAddSubmit}>
              <div className="mb-3">
                <label className="form-label">Level Name *</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.level_name}
                  onChange={(e) =>
                    setFormData({ ...formData, level_name: e.target.value })
                  }
                  placeholder="e.g., Executive Director"
                  required
                />
              </div>
              <div className="mb-3">
                <label className="form-label">Order (1 = top of tree) *</label>
                <input
                  type="number"
                  className="form-control"
                  value={formData.level_order}
                  onChange={(e) =>
                    setFormData({ ...formData, level_order: e.target.value })
                  }
                  min="1"
                  required
                />
              </div>
              <div className="mb-3">
                <label className="form-label">Commission Percentage *</label>
                <div className="input-group">
                  <input
                    type="number"
                    className="form-control"
                    value={formData.commission_percentage}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        commission_percentage: e.target.value,
                      })
                    }
                    min="0"
                    max="100"
                    step="0.01"
                    required
                  />
                  <span className="input-group-text">%</span>
                </div>
                <small className="text-muted">
                  Current total: {parseFloat(totalPercentage).toFixed(1)}% — Remaining:{" "}
                  {(100 - totalPercentage).toFixed(1)}%
                </small>
              </div>
            </form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowAddModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleAddSubmit}>
              Add Level
            </Button>
          </Modal.Footer>
        </Modal>

        {/* ─── Edit Modal ─── */}
        <Modal show={showEditModal} onHide={() => setShowEditModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Edit Level</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <form onSubmit={handleEditSubmit}>
              <div className="mb-3">
                <label className="form-label">Level Name *</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.level_name}
                  onChange={(e) =>
                    setFormData({ ...formData, level_name: e.target.value })
                  }
                  required
                />
              </div>
              <div className="mb-3">
                <label className="form-label">Order *</label>
                <input
                  type="number"
                  className="form-control"
                  value={formData.level_order}
                  onChange={(e) =>
                    setFormData({ ...formData, level_order: e.target.value })
                  }
                  min="1"
                  required
                />
              </div>
              <div className="mb-3">
                <label className="form-label">Commission Percentage *</label>
                <div className="input-group">
                  <input
                    type="number"
                    className="form-control"
                    value={formData.commission_percentage}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        commission_percentage: e.target.value,
                      })
                    }
                    min="0"
                    max="100"
                    step="0.01"
                    disabled={isValid}
                    required
                  />
                  <span className="input-group-text">%</span>
                </div>
                {isValid && (
                  <small className="text-muted">
                    These levels total 100% and are in use for sales. Use "Edit percentages" to change several levels together.
                  </small>
                )}
              </div>
            </form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowEditModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleEditSubmit}>
              Save Changes
            </Button>
          </Modal.Footer>
        </Modal>

        {/* ─── Delete Modal ─── */}
        {showDeleteModal && deletingLevel && (
          <DeleteConfirmationModal
            show={showDeleteModal}
            onClose={() => setShowDeleteModal(false)}
            onConfirm={confirmDelete}
            title="Delete Level"
            message={`Are you sure you want to delete "${deletingLevel.level_name}"? This cannot be undone.`}
          />
        )}
        {/* ─── Default Template Confirmation Modal ─── */}
        {showTemplateConfirm && (
          <ConfirmationModal
            show={showTemplateConfirm}
            onClose={() => setShowTemplateConfirm(false)}
            onConfirm={confirmLoadDefaultTemplate}
            title="Load Default Template"
            message="This will replace existing levels. Are you sure?"
            confirmText="Load Template"
            variant="warning"
          />
        )}
      </div>
    </div>
  );
};

export default MlmLevelsConfigPage;
